'use client';

/**
 * Wiki version history menu items.
 *
 * Rendered inside the wiki sidebar header's "Version history" submenu: the
 * recent versions first, then an expander for the older ones.
 */
import { c as _c } from "react/compiler-runtime";
import { Check, ChevronRight } from 'lucide-react';
import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { WikiGenerationStatus } from '@/generated/prisma/enums';
import { buildWikiPath } from '@/utils/wiki-paths';
export type WikiVersion = {
  id: number;
  version: number;
  created_at: string;
  generation_status?: string;
  is_latest?: boolean;
};
type WikiVersionMenuItemsProps = {
  owner: string;
  repo: string;
  wikiVersions?: WikiVersion[];
  /** Version the reader is on, as resolved by the sidebar header. */
  viewedVersion: number | null;
  latestVersionNumber: number;
};
const RECENT_VERSIONS_LIMIT = 5;
export function WikiVersionMenuItems(t0) {
  const $ = _c(27);
  const {
    owner,
    repo,
    wikiVersions,
    viewedVersion,
    latestVersionNumber
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const timeoutRef = useRef(null);
  const displayVersion = viewedVersion ?? latestVersionNumber;
  let t1;
  bb0: {
    if (!wikiVersions || wikiVersions.length === 0) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          recentVersions: [],
          olderVersions: []
        };
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== wikiVersions) {
      t2 = wikiVersions.slice(0, RECENT_VERSIONS_LIMIT);
      $[1] = wikiVersions;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const recent = t2;
    let t3;
    if ($[3] !== wikiVersions) {
      t3 = wikiVersions.slice(RECENT_VERSIONS_LIMIT);
      $[3] = wikiVersions;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const older = t3;
    let t4;
    if ($[5] !== older || $[6] !== recent) {
      t4 = {
        recentVersions: recent,
        olderVersions: older
      };
      $[5] = older;
      $[6] = recent;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    t1 = t4;
  }
  const {
    recentVersions,
    olderVersions
  } = t1;
  let t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    t3 = [];
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== displayVersion || $[11] !== isNavigating || $[12] !== latestVersionNumber || $[13] !== olderVersions || $[14] !== owner || $[15] !== recentVersions || $[16] !== repo || $[17] !== router || $[18] !== searchParams || $[19] !== showAllVersions) {
    const handleVersionSelect = version => {
      if (isNavigating) {
        return;
      }
      setIsNavigating(true);
      const currentParams = new URLSearchParams(searchParams.toString());
      const newPath = buildWikiPath({
        owner,
        repo,
        version: version && version !== latestVersionNumber ? String(version) : undefined,
        isPublicView: false
      });
      const queryString = currentParams.toString();
      const newUrl = (queryString ? `${newPath}?${queryString}` : newPath) as Route;
      router.push(newUrl);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
        timeoutRef.current = null;
      }, 1000);
    };
    const formatDate = _temp;
    let t6;
    if ($[22] !== latestVersionNumber) {
      t6 = version_0 => {
        const versionText = version_0.version === latestVersionNumber ? `Version ${version_0.version} - Latest` : `Version ${version_0.version}`;
        const statusSuffix = version_0.generation_status === WikiGenerationStatus.failed || version_0.generation_status === WikiGenerationStatus.error ? " (Failed)" : version_0.generation_status === WikiGenerationStatus.pending || version_0.generation_status === WikiGenerationStatus.generating || version_0.generation_status === WikiGenerationStatus.processing ? " (In progress)" : "";
        return versionText + statusSuffix;
      };
      $[22] = latestVersionNumber;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const getVersionLabel = t6;
    const renderVersionItem = version_1 => {
      const isSelected = version_1.version === displayVersion;
      return <DropdownMenuItem key={version_1.id} onClick={() => handleVersionSelect(version_1.version === latestVersionNumber ? null : version_1.version)} className="cursor-pointer"><div className="flex items-center justify-between w-full"><div className="flex flex-col"><span className="text-sm font-medium">{getVersionLabel(version_1)}</span><span className="text-xs text-muted-foreground">{formatDate(version_1.created_at)}</span></div>{isSelected && <Check className="h-4 w-4 text-primary ml-2" />}</div></DropdownMenuItem>;
    };
    t4 = recentVersions.map(version_2 => renderVersionItem(version_2));
    t5 = olderVersions.length > 0 && <><DropdownMenuSeparator />{!showAllVersions ? <DropdownMenuItem onClick={e => {
        e.preventDefault();
        setShowAllVersions(true);
      }} className="cursor-pointer"><div className="flex items-center justify-between w-full"><span className="text-sm text-muted-foreground">{`Show ${olderVersions.length} more version${olderVersions.length > 1 ? "s" : ""}`}</span><ChevronRight className="h-4 w-4 text-muted-foreground ml-2" /></div></DropdownMenuItem> : <><DropdownMenuItem className="text-xs text-muted-foreground py-1" disabled={true}>Older versions</DropdownMenuItem>{olderVersions.map(version_3 => renderVersionItem(version_3))}</>}</>;
    $[10] = displayVersion;
    $[11] = isNavigating;
    $[12] = latestVersionNumber;
    $[13] = olderVersions;
    $[14] = owner;
    $[15] = recentVersions;
    $[16] = repo;
    $[17] = router;
    $[18] = searchParams;
    $[19] = showAllVersions;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  let t6;
  if ($[24] !== t4 || $[25] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
function _temp(dateString) {
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "Unknown date";
    }
    return `${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    })} at ${date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    })}`;
  } catch {
    return "Unknown date";
  }
}